import { useEffect, useState } from 'react';
import { Box, Button, Typography, IconButton, TextField, Card, Grid, Tooltip, CircularProgress } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ClearIcon from '@mui/icons-material/Clear';
import { FileListItem } from '../utils/DataTypes';
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select } from '@mui/material';
import { useDataProvider, useGetIdentity, useNotify } from 'react-admin';
import * as XLSX from 'xlsx';
import { DEFAULTS } from '../constants';

export const SubrogationUploadForm = () => {
  const [fileColumnList, setFileColumnList] = useState<FileListItem[]>([]);
  const [isParsing, setIsParsing] = useState(false);

  const [parsedClaims, setParsedClaims] = useState<any[]>([]);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  const [openConflictDialog, setOpenConflictDialog] = useState(false);
  const [conflictSummary, setConflictSummary] = useState<any>(null);

  const [hasValidationError, setHasValidationError] = useState(false);

  const [availableSheetsByRow, setAvailableSheetsByRow] = useState<Record<number, string[]>>({});

  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [sheetNameByRow, setSheetNameByRow] = useState<Record<number, string>>({});

  const notify = useNotify();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();

  const validateFiles = async (suppressWarnings = false) => {
    if (fileColumnList.length === 0 || !fileColumnList[0].file) {
      if (!suppressWarnings) {
        notify('notifications.medsub.errors.ADD_AT_LEAST_ONE_FILE', { type: 'warning' });
      }
      setHasValidationError(true);
      return false;
    }

    const allValid = fileColumnList.every((item) => item.file && item.column);
    if (!allValid) {
      if (!suppressWarnings) {
        notify('notifications.medsub.errors.FILE_OR_COLUMN_MISSING', { type: 'warning' });
      }
      setHasValidationError(true);
      return false;
    }

    try {
      for (let index = 0; index < fileColumnList.length; index++) {
        const item = fileColumnList[index];

        if (!item.file || !item.column) continue;

        const file = item.file;
        const rawColumn = item.column;
        const columnLetter = item.column.toUpperCase();

        if (!/^[A-Z]+$/.test(columnLetter)) {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.INVALID_COLUMN', {
              type: 'error',
              messageArgs: { column: columnLetter, filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }

        let columnIndex = XLSX.utils.decode_col(columnLetter);
        if (isNaN(columnIndex) || columnIndex < 0) {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.INVALID_COLUMN', {
              type: 'error',
              messageArgs: { column: columnLetter, filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }

        const data = await file.arrayBuffer();
        if (!data || data.byteLength === 0) {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.EMPTY_FILE', {
              type: 'error',
              messageArgs: { filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }

        let workbook;
        try {
          workbook = XLSX.read(data, { type: 'array' });
        } catch {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.INVALID_FILE', {
              type: 'error',
              messageArgs: { filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.INVALID_FILE', {
              type: 'error',
              messageArgs: { filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }

        const sheetName =
          sheetNameByRow[index] && workbook.SheetNames.includes(sheetNameByRow[index])
            ? sheetNameByRow[index]
            : workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet || Object.keys(worksheet).length === 0) {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.NO_SHEET_DATA', {
              type: 'error',
              messageArgs: { filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }

        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 1 });
        if (!rows || rows.length === 0) {
          if (!suppressWarnings) {
            notify('notifications.medsub.errors.NO_CLAIM_DATA', {
              type: 'error',
              messageArgs: { filename: file.name },
            });
          }
          setHasValidationError(true);
          return false;
        }
      }

      setHasValidationError(false);
      return true;
    } catch (error) {
      setHasValidationError(true);
      return false;
    }
  };

  useEffect(() => {
    if (fileColumnList.length === 0) {
      setHasValidationError(false);
      return;
    }
    const runValidation = async () => {
      const isValid = await validateFiles(true);
      setHasValidationError(!isValid);
    };

    runValidation();
  }, [fileColumnList]);

  const handlePreviewClaims = async () => {
    const valid = await validateFiles();
    if (!valid) return;

    setHasValidationError(false);
    setIsParsing(true);
    const allClaims: { claim_number: string; src_filename: string }[] = [];

    try {
      for (let index = 0; index < fileColumnList.length; index++) {
        const item = fileColumnList[index];
        if (!item.file || !item.column) continue;

        const file = item.file;
        const columnLetter = item.column.toUpperCase();
        const src_filename = file.name;

        let columnIndex = -1;
        try {
          columnIndex = XLSX.utils.decode_col(columnLetter);
        } catch {
          notify('notifications.medsub.errors.INVALID_FILE', { type: 'error', messageArgs: { filename: file.name } });
          setHasValidationError(true);
          continue;
        }

        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName =
          sheetNameByRow[index] && workbook.SheetNames.includes(sheetNameByRow[index])
            ? sheetNameByRow[index]
            : workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, range: 1 });
        for (const row of rows) {
          if (row.length > columnIndex) {
            const claimId = row[columnIndex];
            if (claimId !== null && claimId !== undefined && String(claimId).trim() !== '') {
              allClaims.push({
                claim_number: String(claimId).trim(),
                src_filename: src_filename,
              });
            }
          }
        }
      }

      setParsedClaims(allClaims);
      setOpenPreviewDialog(true);
    } catch (error) {
      setParsedClaims([]);
      return;
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileColumnChange = (index: number, key: 'file' | 'column', value: any) => {
    const newList = [...fileColumnList];
    newList[index][key] = value;
    setFileColumnList(newList);
  };

  const handleAddRow = () => setFileColumnList([...fileColumnList, { file: null, column: 'AN' }]);
  const handleDeleteRow = (index: number) => {
    const newList = [...fileColumnList];
    newList.splice(index, 1);
    setFileColumnList(newList);

    setSheetNameByRow((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });

    setAvailableSheetsByRow((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const submitViaS3AndProcess = async (deleteExisting: boolean) => {
    setIsParsing(true);

    try {
      let totalCreated = 0;

      // If multiple files, only delete once (first processed file)
      let deleteFlagForThisRun = deleteExisting;

      for (let index = 0; index < fileColumnList.length; index++) {
        const item = fileColumnList[index];

        if (!item.file || !item.column) continue;

        const file = item.file;
        const claimIdColumn = item.column.toUpperCase();

        // 1) presigned url
        const presignResp = await dataProvider.create('GeneratePresignedUploadUrl', {
          data: {
            fileName: file.name,
            fileType: file.type || 'application/octet-stream',
            folder: 'medsub',
          },
        });

        const { uploadUrl, s3Key } = presignResp.data;

        // 2) upload to S3
        await uploadFileToPresignedUrl(file, uploadUrl);

        // 3) process file from S3 -> DB
        const processResp = await dataProvider.create('ProcessMedSubFile', {
          data: {
            s3Key,
            claimIdColumn,
            sheetName: sheetNameByRow[index],
            createdBy: identity?.fullName || DEFAULTS.UNKNOWN_USER,
            deleteExisting: deleteFlagForThisRun,
          },
        });

        if (!processResp.data?.success) {
          throw new Error(processResp.data?.message || 'Processing failed');
        }

        totalCreated += processResp.data.recordsCreated || 0;

        // after first file, never delete again
        deleteFlagForThisRun = false;
      }

      notify('notifications.medsub.success.SUBMIT_SUCCESS', {
        type: 'success',
        messageArgs: { count: totalCreated },
      });

      setFileColumnList([]);
      setParsedClaims([]);
    } catch (e: any) {
      notify(e?.message || 'Upload/process failed', { type: 'error' });
    } finally {
      setIsParsing(false);
    }
  };

  const handleDeleteAndSubmit = async () => {
    setOpenConflictDialog(false);
    await submitViaS3AndProcess(true);
  };

  const handleDirectSubmit = async () => {
    const valid = await validateFiles();
    if (!valid) {
      notify('notifications.medsub.warnings.FIX_FILE_ERRORS', { type: 'warning' });
      return;
    }
    setIsParsing(true);
    if (fileColumnList.length === 0 || !fileColumnList[0].file) {
      notify('notifications.medsub.errors.ADD_AT_LEAST_ONE_FILE', { type: 'warning' });
      return;
    }
    const allValid = fileColumnList.every((item) => item.file && item.column);
    if (!allValid) {
      notify('notifications.medsub.errors.FILE_OR_COLUMN_MISSING', { type: 'warning' });
      return;
    }

    try {
      const summary = await dataProvider.getOne('MedSubClaimsSummary', { id: 'summary' });

      if (summary.data.count > 0) {
        setConflictSummary(summary.data);
        setOpenConflictDialog(true);
        setIsParsing(false);
      } else {
        await submitViaS3AndProcess(false);
      }
    } catch (error: any) {
      notify('notifications.medsub.errors.FAILED_TO_CHECK_CLAIMS', { type: 'error' });
      setIsParsing(false);
    }
  };

  const getWorkSheetNames = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    return workbook.SheetNames || [];
  };

  const uploadFileToPresignedUrl = async (file: File, uploadUrl: string) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`S3 upload failed: ${res.status} ${res.statusText} ${text}`);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 3,
        boxShadow: 1,
        maxWidth: 900,
        mx: 'auto',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
        Medicaid Subrogation Upload
      </Typography>

      {fileColumnList.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', gap: 2, px: 1, mb: 1 }}>
            <Typography sx={{ flex: 6, fontWeight: 500 }}>File</Typography>
            <Typography sx={{ flex: 4, fontWeight: 500 }}>Claim ID Column</Typography>
            <Typography sx={{ flex: 4, fontWeight: 500 }}>Sheet</Typography>

            <Box sx={{ width: 48 }} />
          </Box>

          {fileColumnList.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Box sx={{ flex: 6 }}>
                <Button
                  variant="contained"
                  component="label"
                  fullWidth
                  startIcon={<FileUploadIcon />}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    py: 1.2,
                    borderRadius: 2,
                  }}
                >
                  {item.file ? item.file.name : 'Choose File'}
                  <input
                    type="file"
                    hidden
                    onChange={async (e) => {
                      if (!e.target.files?.length) return;
                      const file = e.target.files[0];
                      handleFileColumnChange(index, 'file', file);

                      setSheetNameByRow((prev) => {
                        const updated = { ...prev };
                        delete updated[index];
                        return updated;
                      });
                      const sheets = await getWorkSheetNames(file);
                      setAvailableSheetsByRow((prev) => ({
                        ...prev,
                        [index]: sheets,
                      }));

                      const defaultSheet = sheets[0];

                      setSelectedSheet(defaultSheet);
                      setSheetNameByRow((prev) => ({
                        ...prev,
                        [index]: defaultSheet,
                      }));
                      await validateFiles(false);
                    }}
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  />
                </Button>
              </Box>

              <Box sx={{ flex: 4 }}>
                <TextField
                  variant="outlined"
                  label="Column (e.g., AN)"
                  fullWidth
                  value={item.column}
                  size="small"
                  onChange={async (e) => {
                    handleFileColumnChange(index, 'column', e.target.value);
                    await validateFiles(false);
                  }}
                />
              </Box>
              <Box sx={{ flex: 6, mt: 2 }}>
                <Select
                  fullWidth
                  size="small"
                  value={sheetNameByRow[index] || ''}
                  displayEmpty
                  onChange={(e) => {
                    const sheet = e.target.value as string;
                    setSheetNameByRow((prev) => ({
                      ...prev,
                      [index]: sheet,
                    }));
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Sheet
                  </MenuItem>
                  {(availableSheetsByRow[index] || []).map((sheet) => (
                    <MenuItem key={sheet} value={sheet}>
                      {sheet}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Tooltip title="Remove row">
                <IconButton color="error" onClick={() => handleDeleteRow(index)} sx={{ width: 48 }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}

          <Button variant="text" onClick={handleAddRow} sx={{ mt: 1, textTransform: 'none', fontWeight: 500 }}>
            + Add More
          </Button>
        </>
      ) : (
        <>
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              my: 2,
            }}
          >
            <Button variant="contained" size="large" onClick={handleAddRow} startIcon={<FileUploadIcon />}>
              Add File
            </Button>
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2, borderTop: '1px solid #eee', pt: 3 }}>
        <Button
          variant="outlined"
          disabled={isParsing || fileColumnList.length === 0 || !fileColumnList[0].file}
          onClick={handlePreviewClaims}
        >
          Preview Claims
        </Button>
        <Button
          variant="contained"
          disabled={isParsing || hasValidationError || fileColumnList.length === 0 || !fileColumnList[0].file}
          onClick={() => {
            if (hasValidationError) {
              notify('notifications.medsub.warnings.FIX_FILE_ERRORS', { type: 'warning' });
              return;
            }
            handleDirectSubmit();
          }}
        >
          Submit
        </Button>

        {isParsing && <CircularProgress size={24} sx={{ mt: 1 }} />}
      </Box>

      <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Claim ID Preview (Total: {parsedClaims.length})</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Found{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {parsedClaims.length}
            </Box>{' '}
            claims across {fileColumnList.length} file(s).
          </Typography>
          <Typography>Here is a preview of the first 50 claims:</Typography>
          <Box
            sx={{
              p: 2,
              border: '1px solid #ddd',
              borderRadius: 1,
              maxHeight: 300,
              overflowY: 'auto',
              mt: 1,
              backgroundColor: '#f9f9f9',
            }}
          >
            {parsedClaims.slice(0, 50).map((claim, index) => (
              <Typography key={index} sx={{ fontFamily: 'monospace' }}>
                {claim.claim_number}
              </Typography>
            ))}

            {parsedClaims.length > 50 && (
              <Typography variant="caption">...and {parsedClaims.length - 50} more.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenPreviewDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConflictDialog} onClose={() => setOpenConflictDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Existing Claims Found</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            We found{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {conflictSummary?.count}
            </Box>{' '}
            existing claims in the table from the following files:
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Do you want to{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              delete all
            </Box>{' '}
            of these old claims and proceed with your new upload?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenConflictDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteAndSubmit} variant="contained" color="error" autoFocus disabled={isParsing}>
            {isParsing ? 'Processing...' : 'Delete and Upload New Claims'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
